import React from "react"
import { connect } from "react-redux"
import { getFormValues } from "redux-form"
import get from "lodash/get"
import identity from "lodash/identity"
import {
  compose,
  withStateHandlers,
  withHandlers,
  mapProps,
  withPropsOnChange,
  withProps,
} from "recompact"
import moment from "moment"
import storage from "src/helpers/storage"
import makeCompareFunction from "src/helpers/makeCompareFunction"
import { selectRoles } from "src/engine/modules/currentUser"
import CardOverlay from "src/components/Calendar/CardOverlay"
import CardProxy from "src/components/Calendar/CardOverlay/CardProxy"
import GlobalEscapeHandle from "src/components/GlobalEscapeHandle"
import GlobalMouseHandle from "src/components/GlobalMouseHandle"
import NewEventCard from "src/containers/Calendar/components/NewEventCard"
import BlankItem from "src/containers/Calendar/components/BlankItem"
import { ADMIN } from "src/constants/knownRoles"
import { EVENT_CREATION_INITIAL_PARAMS, BLANK_ITEM_ID } from "../constants"
import {
  snapToGrid,
  makeTransparentWithWhite,
  limitDate,
  getTimeLabel,
  getSnapUnit,
} from "../helpers/withItemHelpers"

const FORM_NAME = `createEvent`
const selectValues = getFormValues(FORM_NAME)

const DEFAULT_BACKGROUND_COLOR = `#80b1ff`
const DEFAULT_TEXT_COLOR = `#000000`

// Adds the ability to create items by clicking and stretching the empty calendar space
export default compose(
  mapProps(props => ({
    outerProps: props,
  })),

  withStateHandlers(
    {
      displayModal: false,
      hoverRowIndex: null,
      startDate: null,
      endDate: null,
      selection: false,
    },
    {
      setState: () => identity,
    },
  ),

  // "Momentify" times
  // Flip dates if the end is earlier then the start
  // Round (snap to grid) dates according to scale
  // Prevent the date getting out of the plan's bounds
  // wow, so functional
  withPropsOnChange(
    [`startDate`, `endDate`],
    ({ startDate, endDate, outerProps: { currentScale, plan } }) => ({
      startDate: limitDate(
        snapToGrid(
          moment(startDate > endDate ? endDate : startDate, `x`),
          getSnapUnit(currentScale),
        ),
        plan ? moment(get(plan, `data.startDateTime`)) : null,
        plan ? moment(get(plan, `data.endDateTime`)) : null,
      ),
      endDate: limitDate(
        snapToGrid(
          moment(endDate > startDate ? endDate : startDate, `x`),
          getSnapUnit(currentScale),
        ),
        plan ? moment(get(plan, `data.startDateTime`)) : null,
        plan ? moment(get(plan, `data.endDateTime`)) : null,
      ),
    }),
  ),
  connect(state => ({
    canMakeEventsWithDeletedResources: selectRoles(state)[ADMIN] || false,
  })),
  withHandlers({
    openModal: ({ setState }) => () =>
      setState({
        displayModal: true,
        eventType: null,
      }),
    closeModal: ({ setState }) => () =>
      setState({
        displayModal: false,
        eventType: null,
      }),
    handleMouseUp: ({
      setState,
      selection,
      startDate,
      endDate,
      displayModal,
    }) => () =>
      setState({
        displayModal:
          selection && startDate.diff(endDate) ? true : displayModal,
        selection: false,
      }),
    handleEscapePress: ({ setState }) => () => setState({ selection: false }),
    handleRowMouseDown: ({
      setState,
      canMakeEventsWithDeletedResources,
      outerProps: { plan, groupsCache },
    }) => (rowId, timestamp, layer) =>
      !get(plan, `data.locked`) &&
      (!groupsCache[rowId].isDeleted || canMakeEventsWithDeletedResources) &&
      setState({
        layer,
        hoverRowIndex: rowId,
        startDate: timestamp,
        endDate: timestamp,
        selection: true,
      }),
    handleCanvasMouseMove: ({ setState, selection }) => timestamp =>
      selection &&
      setState({
        endDate: timestamp,
      }),
  }),

  withHandlers({
    handleCreate: ({
      closeModal,
      outerProps,
      outerProps: { handleEventAdd },
    }) => (newEventData, resource) => {
      closeModal(outerProps)
      handleEventAdd(newEventData, [resource])
    },
  }),

  withPropsOnChange(
    // Creates query object for resources() HOC to fetch all involved resources in calendar
    makeCompareFunction([`hoverRowIndex`]),
    ({ hoverRowIndex, outerProps: { groupsCache } }) => {
      if (
        hoverRowIndex !== undefined &&
        groupsCache.length > 0 &&
        !!groupsCache[hoverRowIndex]
      ) {
        const resourceUuid = groupsCache[hoverRowIndex].uuid
        return {
          query: {
            query: `uuid=in=(${resourceUuid})`,
            limit: 1,
          },
        }
      }
      return null
    },
  ),

  // Get the New Item Form values
  connect(state => ({
    formEventTypeId: get(selectValues(state), `eventTypeId`),
    formStartDate: get(selectValues(state), `startDateTime`),
    formEndDate: get(selectValues(state), `endDateTime`),
  })),

  // Replacing dates with form values if there are ones
  withProps(({ startDate, endDate, formStartDate, formEndDate }) => ({
    startDate: (formStartDate && moment(formStartDate)) || startDate,
    endDate: (formEndDate && moment(formEndDate)) || endDate,
  })),

  withProps(props => {
    const {
      outerProps: { groupsCache, eventTypes },
      hoverRowIndex,
      displayModal,
      selection,
      startDate,
      endDate,
      formEventTypeId,
      layer,
    } = props

    const resourceUuid =
      groupsCache[hoverRowIndex] && groupsCache[hoverRowIndex].uuid
    const groupId = groupsCache[hoverRowIndex] && groupsCache[hoverRowIndex].id
    const resource = groupsCache[hoverRowIndex] || {}

    const { color: eventTypeColor, name: eventTypeName } =
      eventTypes.find(one => one.id === formEventTypeId) || {}

    const blankItem =
      selection || displayModal
        ? {
            layer,
            id: BLANK_ITEM_ID,
            group: groupId,
            start_time: startDate,
            end_time: endDate,
            label: eventTypeName || `Новое событие`,
            timeLabel: getTimeLabel(startDate, endDate),
            style: {
              background: makeTransparentWithWhite(
                eventTypeColor || DEFAULT_BACKGROUND_COLOR,
              ),
              color: DEFAULT_TEXT_COLOR,
            },
            Component: BlankItem,
          }
        : null

    return {
      resource,
      resourceUuid,
      startDate,
      endDate,
      blankItem,
      eventTypeName,
    }
  }),

  withHandlers({
    addWithParams: props => params => {
      const {
        resourceUuid,
        outerProps: { onAdd, eventTypes },
        eventTypeName,
      } = props

      const {
        eventTypeId,
        startDateTime: startDate,
        endDateTime: endDate,
      } = params
      const eventType = eventTypes.find(one => one.id === eventTypeId) || {}

      storage.set(EVENT_CREATION_INITIAL_PARAMS, {
        resourceUuid,
        startDate,
        endDate,
        eventTypeId,
        name: eventTypeName,
        color: get(eventType, `color`),
      })
      onAdd()
    },
  }),

  // Disable mouse events on items while creating new item
  withPropsOnChange(
    makeCompareFunction([`selection`, `outerProps.items`]),
    ({ selection, outerProps: { items } }) => ({
      items: items.map(({ style, ...rest }) => ({
        style: {
          ...style,
          ...(selection ? { pointerEvents: `none` } : {}),
        },
        ...rest,
      })),
    }),
  ),

  // Put the blank item to the calendar if it should be putted
  withProps(props => {
    const { items, blankItem } = props

    return {
      items: [...(blankItem ? [blankItem] : []), ...items],
    }
  }),

  // Adding the pre-create modal
  BaseComponent => props => {
    const {
      outerProps,
      outerProps: { planId },
      handleCreate,
      addWithParams,
      displayModal,
      closeModal,
      startDate,
      endDate,
      resource,
      handleMouseUp,
      handleRowMouseDown,
      handleCanvasMouseMove,
      handleEscapePress,
      items,
    } = props

    const card = displayModal && (
      <CardProxy
        planId={planId}
        onExpand={addWithParams}
        onClose={closeModal}
        newEventParams={{
          resource,
          startDateTime: startDate,
          endDateTime: endDate,
        }}
      />
    )

    return (
      <CardOverlay
        CardComponent={NewEventCard}
        handleItemAdd={data => {
          handleCreate(data, resource)
        }}
      >
        <BaseComponent
          {...outerProps}
          items={items}
          onRowMouseDown={handleRowMouseDown}
          onCanvasMouseMove={handleCanvasMouseMove}
        />
        {card}
        <GlobalEscapeHandle onEscapePress={handleEscapePress} />
        <GlobalMouseHandle onMouseUp={handleMouseUp} />
      </CardOverlay>
    )
  },
)
